import type {StockScope} from '../../common/stockScope'
/**
 * Multi-supplier offer types. Mirrors the Lumen
 * /partner-portal/offers/* response shape verbatim.
 *
 * Catalog ownership: NONE of these fields touch oc_product (name,
 * description, images, category, brand). Catalog content is
 * admin-owned; suppliers edit only the OFFER side. A "Suggest a
 * Catalog Change" workflow is a Phase 2 surface.
 */

export type DeliveryPath = 'in_warehouse' | 'pickup' | 'supplier_ship'

export interface IOfferModel {
  product_supplier_id: number
  product_id: number
  barcode: string | null
  product_name: string | null
  image: string | null
  /** Decimal as string from MySQL (e.g. "10.0000"). Parse for math. */
  price: string
  /** Decimal as string from MySQL. */
  cost: string
  /** At-supplier stock (what the supplier holds, not yet in FC). */
  supplier_quantity: number
  /** Legacy total field — may be absent post multi-supplier rework; prefer supplier_quantity. */
  quantity?: number
  warehouse_quantity: number
  /** "shop" when the supplier runs a partner shop: the quantity it manages is `shop_quantity`. */
  stock_scope?: StockScope
  /** What the supplier's shop can sell now (its shelf) — set only when stock_scope is "shop". */
  shop_quantity?: number
  supplier_sku: string | null
  /** 1 = the offer's product is a PACKAGE: its price and quantity are derived
   *  from the package's components, so neither may be edited here. */
  is_offer?: number
  /** MySQL tinyint — treat as truthy. */
  is_active: 0 | 1
  /** Eligibility used by Active filters/cards: enabled, priced, and in stock.
   *  Absent from an API that predates DEV-3826. */
  is_competing?: 0 | 1
  /** Holding the buy box — the backend only sets it on a competing offer. */
  is_winning_offer: 0 | 1
  /** Routing-effective hours (0 = ships from FC same-day). */
  effective_delivery_hours: number
  effective_delivery_path: DeliveryPath
  /** Boolean only — the override VALUE never leaves the backend. */
  admin_override_active: boolean
  last_stock_update: string | null
  date_added?: string
  date_modified: string
}

export interface IOfferSummary {
  total_offers: number
  active_offers: number
  paused_offers: number
  winning_offers: number
  out_of_stock_offers: number
  unpriced_offers: number
  low_stock_offers: number
  total_units: number
  warehouse_units: number
}

export interface IOfferPerformance {
  reliability_score: number
  avg_lead_days: number
  min_lead_days: number
  max_lead_days: number
  stddev_lead_days: number
  po_count: number
  last_po_date: string | null
  winning_offer_rate: number
  active_offers: number
  winning_offers: number
  low_stock_offers: number
  out_of_stock_offers: number
}

export interface IOfferUpdatePayload {
  price?: number
  /** At-supplier quantity — replaces legacy 'quantity' to match the
   *  backend allowlist after the multi-supplier per-offer refactor. */
  supplier_quantity?: number
  supplier_sku?: string | null
  is_active?: 0 | 1
  // Intentionally NO `cost` — server derives from price × supplier margin.
  // Intentionally NO `warehouse_quantity` — FC receiving process sets it.
}

/** One side of the buy-box comparison (mine or the anonymized winner). */
export interface IOfferMetric {
  effective_price: number
  delivery_hours: number
  adjusted_price: number
}

/**
 * Anonymized "why didn't I win the buy box" breakdown
 * (GET /offers/{id}/why-not-winning). `winner` carries ONLY price/delivery
 * metrics — never the competitor's identity.
 */
export interface IWhyNotWinning {
  is_winning: boolean
  is_active: boolean
  in_stock: boolean
  my_offer: IOfferMetric
  winner: IOfferMetric | null
  reasons: string[]
  /** Price that would win on the price tier, or null when the winner is
   *  simply faster (a price cut can't win then). */
  price_to_win: number | null
  /** Currency code for the prices in this breakdown. Optional for backward
   *  compatibility with responses that predate multi-currency. */
  currency?: string
}

export type OfferStatusFilter = 'active' | 'paused' | 'out_of_stock' | 'unpriced' | 'all'

// Order-by keys — must match the whitelist in SupplierOfferModel::listOffers.
// '' (default) = the buy-box-first ordering the backend falls back to.
export type OfferSortKey =
  | 'price_asc'
  | 'price_desc'
  | 'stock_asc'
  | 'stock_desc'
  | 'name_asc'
  | 'name_desc'
  | 'updated_desc'

/** Dropdown options for the offers "Order by" control (value '' = default). */
export const OFFER_SORT_OPTIONS: {value: string; labelKey: string}[] = [
  {value: '', labelKey: 'offers.sort.default'},
  {value: 'price_asc', labelKey: 'offers.sort.price_asc'},
  {value: 'price_desc', labelKey: 'offers.sort.price_desc'},
  {value: 'stock_asc', labelKey: 'offers.sort.stock_asc'},
  {value: 'stock_desc', labelKey: 'offers.sort.stock_desc'},
  {value: 'name_asc', labelKey: 'offers.sort.name_asc'},
  {value: 'name_desc', labelKey: 'offers.sort.name_desc'},
  {value: 'updated_desc', labelKey: 'offers.sort.updated_desc'},
]

export interface IOfferListFilters {
  page?: number
  limit?: number
  status?: OfferStatusFilter
  winning_only?: boolean
  not_winning?: boolean
  search?: string
  sort?: OfferSortKey
}

export interface IOfferListResponse {
  data: IOfferModel[]
  total: number
  page: number
  limit: number
  pages: number
}

/** Localized labels for the delivery-path chip. UI maps via DELIVERY_PATH_KEYS. */
export const DELIVERY_PATH_KEYS: Record<DeliveryPath, string> = {
  in_warehouse: 'offers.delivery_path.in_warehouse',
  pickup: 'offers.delivery_path.pickup',
  supplier_ship: 'offers.delivery_path.supplier_ship',
}

// ─────────────────────────────────────────────────────────────────
// Match-by-barcode onboarding ("Sell Yours")
//
// The Add-Product wizard's barcode field calls /offers/lookup-barcode
// before the user fills any other field. The tri-state response
// branches the UI:
//
//   available     → continue with the full product-request wizard
//                   (legacy AddProduct flow, untouched)
//   match         → render an inline catalog preview + "Add my offer"
//                   CTA that navigates to /offers/from-barcode/:barcode
//   already_yours → notice + deep-link to /offers/list?search=<barcode>
// ─────────────────────────────────────────────────────────────────

export type BarcodeLookupState =
  | 'available'
  | 'match'
  | 'already_yours'
  | 'multiple_match'
  | 'pending_request'

export interface ICatalogPreview {
  product_id: number
  barcode: string
  name: string | null
  image: string | null
  brand: string | null
  /** Localized size/color labels (resolved server-side) so the single-match
   *  card can show the product's variant too. null when not applicable. */
  size_label?: string | null
  color_label?: string | null
  /** Optional — present on responses that include the supplier-aware
   *  cost calc context (lookup-barcode). 0..100 percent.
   *  Cost preview formula: cost = price × (1 − margin_percent / 100). */
  margin_percent?: number
  manufacturer_id?: number
  /** Anonymized — count only, never names/prices. */
  other_suppliers_count: number
}

/**
 * Multi-variant catalog preview, returned per-product in the
 * `multiple_match` lookup state. Adds size/color labels (resolved
 * server-side from oc_option_value_description) and the supplier's
 * own ownership context per variant.
 */
export interface ICatalogVariantPreview extends ICatalogPreview {
  master: number
  size_id: number
  color_id: number
  /** Localized label (e.g. "S", "M", "Black"). null when not applicable. */
  size_label: string | null
  color_label: string | null
  /** True when the calling supplier already has an offer on THIS variant. */
  has_my_offer: boolean
  /** Set when has_my_offer=true so the UI can deep-link to that offer. */
  product_supplier_id: number | null
}

export type IBarcodeLookupResult =
  | {state: 'available'; barcode: string}
  | {state: 'match'; product: ICatalogPreview}
  | {state: 'already_yours'; product: ICatalogPreview; product_supplier_id: number}
  | {state: 'multiple_match'; products: ICatalogVariantPreview[]}
  // Already submitted to the new-product request queue (pending review),
  // not yet in the catalog — so neither "add product" nor "add offer" applies.
  | {state: 'pending_request'; barcode: string}

export interface IFromBarcodePayload {
  barcode: string
  /** Optional — required when the barcode maps to multiple catalog
   *  rows (size/color variants). The chooser passes the supplier's
   *  pick; backend validates it actually matches the barcode. */
  product_id?: number
  price: number
  quantity: number
  supplier_sku?: string | null
  // Intentionally NO `cost` — server derives from price × supplier margin.
  // Intentionally NO `warehouse_quantity` — FC receiving process sets it.
}

/**
 * "Add a variant on a used barcode" — request a size/color that isn't in
 * the catalog yet, under a barcode that is. The server copies all catalog
 * content from the master and derives the master itself; the supplier only
 * supplies the variation (existing option values) + offer seed values.
 * This files a product REQUEST (admin-reviewed) — the offer is created on
 * approval, so there's no instant offer back.
 */
export interface IVariantRequestPayload {
  barcode: string
  /** option_value_id; -1/omitted when this axis is unchanged from the master. */
  size_id?: number
  color_id?: number
  price: number
  quantity: number
  /** New-COLOR variants only: photos for the new colour. Sent as multipart
   *  product_image[]; a size-only variant reuses the master's images. */
  images?: File[]
}

// Catalog edits: every supplier with an active offer can request a
// catalog edit via the standard `/products/product-edit/:id` form,
// which routes through `pp_edit_product_request` for catalog-team
// review. The earlier per-field "Suggest a Catalog Change" dialog
// was rolled back — see commit log for rationale (one queue, one
// UX, the team prefers the full form).
